<template>
  <div class="page-wrap">
    <v-snackbar
      v-model="snackbar"
      :timeout="snackbarTimeout"
      top
      right
      class="notification"
      :color="snackbarColor"
      elevation="6"
    >
      <div class="d-flex align-center">
        <v-icon left color="white" class="mr-2">{{ snackbarIcon }}</v-icon>
        <span>{{ snackbarText }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <v-container class="inner-wrap">
      <v-row>
        <!-- Contact Information Card -->
        <v-col cols="12" md="5" lg="4">
          <v-card class="contact-info-card" elevation="8">
            <div class="contact-info-content">
              <div class="text-center mb-8">
                <v-icon size="64" class="mb-4 text-white">mdi-message-text</v-icon>
                <h3 class="use-text-title2 mb-2">{{ $t('contact.contact_title') }}</h3>
                <p class="text-subtitle-1">{{ $t('contact.dec') }}</p>
              </div>

              <div class="contact-details">
                <div class="contact-item">
                  <div class="contact-icon-wrapper">
                    <v-icon color="primary" size="28">mdi-email</v-icon>
                  </div>
                  <div class="contact-text">
                    <h4 class="text-subtitle-2 mb-1">{{ $t('contact.email_label') }}</h4>
                    <a href="mailto:contact@motanamy.com" class="contact-link">
                      contact@motanamy.com
                    </a>
                  </div>
                </div>

                <div class="contact-item">
                  <div class="contact-icon-wrapper">
                    <v-icon color="primary" size="28">mdi-phone</v-icon>
                  </div>
                  <div class="contact-text">
                    <h4 class="text-subtitle-2 mb-1">{{ $t('contact.phone_label') }}</h4>
                    <a href="tel:+201558025577" class="contact-link" dir="ltr">
                      +20 155 802 5577
                    </a>
                  </div>
                </div>

                <div class="contact-item">
                  <div class="contact-icon-wrapper">
                    <v-icon color="primary" size="28">mdi-whatsapp</v-icon>
                  </div>
                  <div class="contact-text">
                    <h4 class="text-subtitle-2 mb-1">{{ t('contact.whatsapp_label', 'واتساب', 'WhatsApp') }}</h4>
                    <a href="https://wa.me/201558025577" target="_blank" class="contact-link" dir="ltr">
                      +20 155 802 5577
                    </a>
                  </div>
                </div>

                <div class="contact-item">
                  <div class="contact-icon-wrapper">
                    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                    </svg>
                  </div>
                  <div class="contact-text">
                    <h4 class="text-subtitle-2 mb-1">{{ t('contact.telegram_label', 'تيليجرام', 'Telegram') }}</h4>
                    <a href="https://t.me/MotanamySupport_bot" target="_blank" rel="noopener noreferrer" class="contact-link" dir="ltr">
                      @MotanamySupport_bot
                    </a>
                  </div>
                </div>

                <div class="contact-item">
                  <div class="contact-icon-wrapper">
                    <v-icon color="primary" size="28">mdi-clock-outline</v-icon>
                  </div>
                  <div class="contact-text">
                    <h4 class="text-subtitle-2 mb-1">{{ $t('contact.working_hours_label') }}</h4>
                    <p class="mb-0">{{ $t('contact.working_days') }}</p>
                    <p class="mb-0">{{ $t('contact.working_time') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Contact Form Card -->
        <v-col cols="12" md="7" lg="8">
          <v-card :class="[{ 'map-form': full }]" class="form-box" elevation="0">
            <div class="form-accent-strip"></div>
            <div class="form">
              <div class="text-center mb-6">
                <div class="section-kicker-bar mb-3">
                  <v-chip small outlined color="primary" class="kicker-chip">
                    <v-icon small left>mdi-email-outline</v-icon>
                    {{ $t('contact.send_message_title') }}
                  </v-chip>
                </div>
                <h4 class="use-text-title2 mb-2">
                  {{ $t('contact.send_message_title') }}
                </h4>
                <p class="form-subtitle">{{ $t('contact.send_message_subtitle') }}</p>
              </div>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row class="spacing6">
                  <!-- Selected Service Badges (if provided via props) -->
                  <v-col cols="12" class="px-6" v-if="items && items.length > 0">
                    <div class="service-badges-box mb-1">
                      <span class="text-caption font-weight-bold d-block mb-1">
                        <v-icon x-small color="primary">mdi-shape-plus</v-icon>
                        {{ $t('contact.selectService') }}
                      </span>
                      <v-chip
                        v-for="(item, idx) in items"
                        :key="idx"
                        small
                        color="primary"
                        outlined
                        class="service-chip mr-2 mb-1"
                      >
                        <v-icon x-small left>mdi-check-circle-outline</v-icon>
                        {{ item }}
                      </v-chip>
                    </div>
                  </v-col>

                  <!-- Name -->
                  <v-col cols="12" sm="6" class="px-6">
                    <v-text-field 
                      v-model="form.name" 
                      :rules="nameRules" 
                      :label="$t('contact.form_name')" 
                      required 
                      outlined
                      dense
                      prepend-inner-icon="mdi-account"
                    />
                  </v-col>

                  <!-- Email -->
                  <v-col cols="12" sm="6" class="px-6">
                    <v-text-field 
                      v-model="form.email" 
                      :rules="emailRules" 
                      :label="$t('contact.form_email')" 
                      required 
                      outlined
                      dense
                      prepend-inner-icon="mdi-email"
                    />
                  </v-col>

                  <!-- Department -->
                  <v-col cols="12" sm="6" class="px-6">
                    <v-select
                      v-model="form.department"
                      :items="departmentOptions"
                      item-text="label"
                      item-value="value"
                      :label="t('contact.form_department', 'القسم', 'Department')"
                      outlined
                      dense
                      prepend-inner-icon="mdi-domain"
                    />
                  </v-col>

                  <!-- Phone -->
                  <v-col cols="12" sm="6" class="px-6">
                    <v-text-field 
                      v-model="form.phone" 
                      :label="$t('contact.form_phone')" 
                      outlined
                      dense
                      prepend-inner-icon="mdi-phone"
                    />
                  </v-col>

                  <!-- Subject -->
                  <v-col cols="12" class="px-6">
                    <v-text-field 
                      v-model="form.subject" 
                      :rules="subjectRules" 
                      :label="t('contact.form_subject', 'الموضوع *', 'Subject *')" 
                      required 
                      outlined
                      dense
                      prepend-inner-icon="mdi-format-title"
                    />
                  </v-col>

                  <!-- Message -->
                  <v-col cols="12" class="px-6">
                    <v-textarea 
                      v-model="form.message" 
                      :rules="messageRules"
                      rows="5" 
                      :label="$t('contact.form_message')" 
                      required
                      outlined
                      prepend-inner-icon="mdi-message-text"
                    />
                  </v-col>
                </v-row>
                <div class="form-control-label">
                  <v-checkbox 
                    v-model="checkbox" 
                    color="secondary" 
                    :rules="termsRules" 
                    :label="$t('contact.form_terms')"
                    required 
                  />
                  <router-link :to="localePath('/policy/privacy')" class="link">
                    {{ $t('contact.form_privacy') }}
                  </router-link>
                </div>
                <div class="btn-area">
                  <v-btn 
                    large 
                    block 
                    color="primary" 
                    @click="validate"
                    :loading="loading"
                    :disabled="loading"
                    depressed
                    class="submit-btn"
                  >
                    <v-icon left>mdi-send</v-icon>
                    {{ $t('contact.form_send') }}
                  </v-btn>
                </div>
              </v-form>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style lang="scss" scoped>
@import './form-style.scss';

.contact-info-card {
  height: 100%;
  background: linear-gradient(135deg, #3f51b5 0%, #283593 100%);
  color: white !important;
  border-radius: 16px !important;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(244, 67, 54, 0.3) 0%, transparent 70%);
    border-radius: 50%;
    transform: translate(50%, -50%);
  }
  
  .contact-info-content {
    padding: 40px 30px;
    position: relative;
    z-index: 1;
    
    @media (max-width: 960px) {
      padding: 30px 20px;
    }
  }
  
  h3, h4, p {
    color: white !important;
  }
  
  .text-subtitle-1 {
    opacity: 0.9;
  }
}

.contact-details {
  margin-top: 40px;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  gap: 16px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(-5px);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
}

.contact-icon-wrapper {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.4) 0%, rgba(244, 67, 54, 0.2) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  // margin-left: 16px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
  
  .v-icon,
  svg {
    color: white !important;
    fill: white !important;
  }
}

.contact-text {
  flex: 1;
  
  h4 {
    font-weight: 600;
    margin-bottom: 4px;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 14px;
  }
}

.contact-link {
  color: white !important;
  text-decoration: none !important;
  font-size: 15px;
  font-weight: 500;
  display: inline-block;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.8;
    transform: translateX(-3px);
  }
}

.form-box {
  border-radius: 20px !important;
  background: linear-gradient(145deg, rgba(255,255,255,0.97), rgba(248,250,255,0.92)) !important;
  border: 1px solid rgba(63, 81, 181, 0.12) !important;
  box-shadow: 0 8px 28px rgba(36, 54, 128, 0.09) !important;
  overflow: hidden;
  position: relative;

  .form-accent-strip {
    height: 4px;
    background: linear-gradient(90deg, #3f51b5, #f44336);
    border-radius: 20px 20px 0 0;
  }

  .form {
    padding: 36px 30px 50px;

    @media (max-width: 960px) {
      padding: 28px 20px 40px;
    }
  }
}

.section-kicker-bar {
  display: flex;
  justify-content: center;
  .kicker-chip {
    font-weight: 600;
    letter-spacing: 0.5px;
    font-size: 0.78rem;
    border-radius: 20px;
  }
}

.form-subtitle {
  font-size: 0.95rem;
  color: rgba(30, 30, 60, 0.55);
  line-height: 1.6;
}

.submit-btn {
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  background: linear-gradient(90deg, #3f51b5, #5c6bc0) !important;
  transition: all 0.3s ease !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(63, 81, 181, 0.35) !important;
    background: linear-gradient(90deg, #3f51b5, #f44336) !important;
  }
}

.inner-wrap {
  padding-top: 40px;
  padding-bottom: 40px;
}

::v-deep {
  .v-text-field--outlined fieldset,
  .v-textarea--outlined fieldset,
  .v-select--outlined fieldset {
    border-color: rgba(63, 81, 181, 0.18) !important;
    border-width: 1px !important;
    transition: border-color 0.25s ease !important;
  }
  
  .v-text-field--outlined:hover fieldset,
  .v-textarea--outlined:hover fieldset,
  .v-select--outlined:hover fieldset {
    border-color: rgba(63, 81, 181, 0.45) !important;
  }
  
  .v-input--is-focused fieldset {
    border-color: #3f51b5 !important;
    border-width: 2px !important;
  }

  .v-label {
    color: rgba(30, 30, 60, 0.55) !important;
    font-size: 0.9rem;
  }

  .v-label--active {
    color: #3f51b5 !important;
  }
  
  .v-text-field.v-input--dense,
  .v-select.v-input--dense {
    margin-bottom: 8px;
  }
}

.service-badges-box {
  background: rgba(63, 81, 181, 0.05);
  border: 1px dashed rgba(63, 81, 181, 0.3);
  padding: 10px 14px;
  border-radius: 10px;
}
</style>

<script>
import { submitContactMessage } from '~/services/contact'

export default {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    type: {
      type: Number,
      default: () => 0
    },
    full: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valid: true,
      loading: false,
      snackbar: false,
      snackbarColor: 'success',
      snackbarIcon: 'mdi-check-circle',
      snackbarText: '',
      snackbarTimeout: 5000,
      form: {
        name: '',
        email: '',
        department: this.items && this.items.length > 0 ? 'Sales' : 'General',
        phone: '',
        subject: '',
        message: ''
      },
      checkbox: false
    }
  },
  computed: {
    departmentOptions() {
      return [
        { value: 'General', label: this.t('contact.dept_general', 'عام', 'General') },
        { value: 'Sales', label: this.t('contact.dept_sales', 'المبيعات', 'Sales') },
        { value: 'Support', label: this.t('contact.dept_support', 'الدعم الفني', 'Support') }
      ]
    },
    nameRules() {
      return [v => (!!v && !!v.trim()) || this.t('contact.name_required', 'الاسم مطلوب', 'Name is required')]
    },
    emailRules() {
      return [
        v => (!!v && !!v.trim()) || this.t('contact.email_required', 'البريد الإلكتروني مطلوب', 'Email is required'),
        v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || this.t('contact.email_invalid', 'يرجى إدخال بريد إلكتروني صحيح', 'Please enter a valid email address')
      ]
    },
    subjectRules() {
      return [v => (!!v && !!v.trim()) || this.t('contact.subject_required', 'موضوع الرسالة مطلوب', 'Subject is required')]
    },
    messageRules() {
      return [v => (!!v && !!v.trim()) || this.t('contact.message_required', 'نص الرسالة مطلوب', 'Message is required')]
    },
    termsRules() {
      return [v => !!v || this.t('contact.terms_required', 'يجب الموافقة على سياسة الخصوصية والاستخدام', 'You must agree to the privacy and usage policy')]
    }
  },
  mounted() {
    if (this.items && this.items.length > 0) {
      this.form.department = 'Sales'
    }
  },
  methods: {
    t(key, fallbackAr, fallbackEn) {
      if (this.$te && this.$te(key)) {
        const val = this.$t(key)
        if (val && val !== key) {
          return val
        }
      }
      return this.$i18n && this.$i18n.locale === 'en' ? fallbackEn : fallbackAr
    },
    async validate() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true

      try {
        const result = await submitContactMessage(this.$axios, {
          name: this.form.name,
          email: this.form.email,
          department: this.form.department,
          subject: this.form.subject,
          message: this.form.message,
          phone: this.form.phone,
          phoneLabel: this.t('contact.phone_append_label', 'رقم الهاتف', 'Phone Number'),
          items: this.items,
          itemsLabel: this.t('contact.selectService', 'الخدمة المطلوبة', 'Service you need')
        })

        if (result.success) {
          this.snackbarColor = 'success'
          this.snackbarIcon = 'mdi-check-circle'
          this.snackbarText = this.t('contact.success_message', 'وصلت. شكراً لك.', 'Received. Thank you.')
          this.snackbarTimeout = 4500
          this.snackbar = true

          this.$refs.form.reset()
          this.form = {
            name: '',
            email: '',
            department: this.items && this.items.length > 0 ? 'Sales' : 'General',
            phone: '',
            subject: '',
            message: ''
          }
          this.checkbox = false
        } else if (result.isRateLimited) {
          this.snackbarColor = 'warning'
          this.snackbarIcon = 'mdi-clock-alert-outline'
          this.snackbarText = this.t(
            'contact.rate_limit_error',
            'لقد وصلت إلى الحد الأقصى لعدد المحاولات. يرجى الانتظار والمحاولة لاحقاً.',
            'You have reached the maximum number of submissions. Please wait before trying again.'
          )
          this.snackbarTimeout = 6500
          this.snackbar = true
        } else {
          this.snackbarColor = 'error'
          this.snackbarIcon = 'mdi-alert-circle-outline'
          this.snackbarText = this.t(
            'contact.general_error',
            'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
            'Failed to send message. Please try again.'
          )
          this.snackbarTimeout = 6500
          this.snackbar = true
        }
      } catch (err) {
        this.snackbarColor = 'error'
        this.snackbarIcon = 'mdi-alert-circle-outline'
        this.snackbarText = this.t(
          'contact.general_error',
          'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
          'Failed to send message. Please try again.'
        )
        this.snackbarTimeout = 6500
        this.snackbar = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
